// Pure, render-independent orchestration for the Stripe pay-step click.
//
// The payment click handler previously lived inline in checkout-form.tsx as an
// un-testable async function with two structural defects:
//   1. `await createStripePayment(...)` was not wrapped in try/catch, so any
//      thrown/rejected payment (backend 4xx/5xx, network drop, non-JSON body,
//      Stripe.js not loaded) skipped the single `sendPaymentIsProcessing(false)`
//      reset — leaving the button stuck on "Processing..." forever, silently.
//   2. The processing flag was turned ON unconditionally but only turned OFF
//      inside a leaky debounce `if`, so a second click within the debounce
//      window locked the UI.
// It also had no timeout, so a hung backend hung the UI indefinitely.
//
// This module extracts the decision (shouldAcceptPaymentClick) and the
// orchestration (handlePaymentAttempt) as pure, dependency-injected functions
// so every failure path can be reproduced in node:test. Mirrors the
// wizard-step-gate.ts extraction.

export type AcceptReason = 'ok' | 'debounced' | 'in-flight' | 'stripe-not-ready'

export interface AcceptPaymentClickInput {
  /** timeStamp of the last click we actually processed (null if none yet) */
  lastAcceptedTimestamp: number | null
  /** DOM event timeStamp of the current click */
  eventTimestamp: number
  /** debounce window in ms (3000 in production) */
  debounceMs: number
  /** true while a payment attempt is already running */
  inFlight: boolean
  /** true only once Stripe.js + Elements have loaded */
  stripeReady: boolean
}

export interface AcceptPaymentClickResult {
  accept: boolean
  reason: AcceptReason
}

/**
 * Whether a Pay click should start a payment attempt. Pure: safe to call in a
 * handler and to unit test. Fixes Group C (debounce) and Group B (readiness):
 * the processing flag must only be turned on when this returns accept: true.
 */
export const shouldAcceptPaymentClick = (
  input: AcceptPaymentClickInput,
): AcceptPaymentClickResult => {
  if (!input.stripeReady) return {accept: false, reason: 'stripe-not-ready'}
  if (input.inFlight) return {accept: false, reason: 'in-flight'}
  if (
    input.lastAcceptedTimestamp !== null
    && input.eventTimestamp - input.lastAcceptedTimestamp < input.debounceMs
  ) {
    return {accept: false, reason: 'debounced'}
  }
  return {accept: true, reason: 'ok'}
}

export interface PaymentResult {
  success: boolean
  message?: string
}

export type PaymentOutcome =
  | 'succeeded' | 'declined' | 'errored' | 'timed-out' | 'submission-failed'

export interface PaymentAttemptDeps {
  /**
   * Performs tokenization + backend checkout; may resolve, reject, or never
   * settle. Receives an AbortSignal that is aborted when the UI timeout wins the
   * race, so the underlying checkout can be cancelled and can't complete a late
   * charge after the user has been told it timed out.
   */
  createPayment: (signal: AbortSignal) => Promise<PaymentResult>
  /** toggles the shared paymentIsProcessing flag */
  setProcessing: (on: boolean) => void
  /** clears the Stripe CardElement */
  clearCard: () => void
  /**
   * Records the submission/vote after a successful charge and resolves whether
   * it was actually recorded. Resolves false when the vote could not be built
   * or the persist call failed — in which case the submission path has already
   * alerted the user + flagged reconciliation, and we must NOT advance to the
   * success step (see resolveSubmissionOutcome.showSuccessStep).
   */
  castSubmission: () => Promise<boolean>
  /** advances the wizard to the success step; only called when the submission recorded */
  advance: () => void
  /** surfaces a message to the user (replaces the silent throw) */
  notifyUser: (message: string) => void
  logError: (message: string, data?: unknown) => void
  /** hard cap so a hung backend still releases the UI */
  timeoutMs: number
  /** injectable timer so timeout behavior is deterministically testable */
  delay?: (ms: number) => Promise<void>
}

export interface PaymentAttemptResult {
  outcome: PaymentOutcome
  submissionCast: boolean
  userNotified: boolean
}

/**
 * The paymentIsProcessing flag lives in the gallery component, above the wizard.
 * Closing the wizard (the X button) previously left a stuck `true` in place, so
 * reopening showed a permanently-disabled Pay step recoverable only by a full
 * page reload. Closing must always reset the flag. Pure so the invariant is
 * testable and self-documenting.
 */
export const shouldResetPaymentProcessingOnClose = (): boolean => true

const TIMEOUT = Symbol('payment-timeout')

const defaultDelay = (ms: number): Promise<void> =>
  new Promise<void>(resolve => setTimeout(resolve, ms))

// Shared user-facing messages so the two timeout paths (this module's race and
// lib/api's AbortController) read identically no matter which timer wins.
const TIMEOUT_MESSAGE =
  'Your payment is taking longer than expected. Please check your email for a receipt before retrying.'
const GENERIC_ERROR_MESSAGE =
  'We could not complete your payment. If you were charged, please contact support before retrying.'
// Charge succeeded but recording the entry timed out — the user must be told
// (money likely taken, don't resubmit) rather than seeing the spinner clear
// with no feedback.
const SUBMISSION_TIMEOUT_MESSAGE =
  'Your payment went through, but we could not confirm your entry was recorded. Please check your email and contact support before retrying — do not resubmit.'

// The aborted-fetch rejection surfaces as something named 'AbortError' — an
// Error in Node but a DOMException in browsers, which is NOT an Error subclass
// there. Match on the `name` property of any object rather than instanceof so
// aborts aren't misclassified as generic errors.
const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null
  && (error as {name?: unknown}).name === 'AbortError'

/**
 * Runs a single (already-accepted) payment attempt. OWNS the processing flag so
 * we can guarantee it is always reset (Group A/B invariant): setProcessing(true)
 * on entry, setProcessing(false) + clearCard() in `finally` no matter how the
 * payment call settles — resolve, reject, or timeout.
 */
export const handlePaymentAttempt = async (
  deps: PaymentAttemptDeps,
): Promise<PaymentAttemptResult> => {
  const delay = deps.delay ?? defaultDelay
  let outcome: PaymentOutcome = 'errored'
  let submissionCast = false
  let userNotified = false

  // Owns cancellation: aborted when the timeout wins so createPayment's
  // underlying checkout is cancelled rather than left running to complete a late
  // charge after we've already reported a timeout.
  const abortController = new AbortController()

  deps.setProcessing(true)
  try {
    const raced = await Promise.race<PaymentResult | typeof TIMEOUT>([
      deps.createPayment(abortController.signal),
      delay(deps.timeoutMs).then(() => TIMEOUT),
    ])

    if (raced === TIMEOUT) {
      abortController.abort()
      outcome = 'timed-out'
      deps.notifyUser(TIMEOUT_MESSAGE)
      userNotified = true
      deps.logError('PURCHASE-F_TIMEOUT: payment call exceeded timeout', {timeoutMs: deps.timeoutMs})
    } else if (raced?.success) {
      // Charge succeeded; only advance to the success step once the submission
      // path confirms the entry was recorded. Bound this step too — if it hangs
      // (e.g. addVote never settles) we must still reach finally and reset the
      // "Processing..." state rather than reintroducing the stuck spinner. A
      // false result (build/persist failure) has already notified the user +
      // flagged reconciliation; a timeout is logged. Either way we do NOT show a
      // false success.
      const recorded = await Promise.race<boolean | typeof TIMEOUT>([
        deps.castSubmission(),
        delay(deps.timeoutMs).then(() => TIMEOUT),
      ])
      submissionCast = recorded === true
      if (recorded === true) {
        outcome = 'succeeded'
        deps.advance()
      } else {
        outcome = 'submission-failed'
        if (recorded === TIMEOUT) {
          // No downstream alert fired (the cast never settled), so notify here.
          deps.notifyUser(SUBMISSION_TIMEOUT_MESSAGE)
          userNotified = true
          deps.logError('PURCHASE-F_SUBMIT_TIMEOUT: submission recording exceeded timeout', {timeoutMs: deps.timeoutMs})
        }
      }
    } else {
      outcome = 'declined'
      const message = raced?.message || 'Payment failed'
      deps.notifyUser(message)
      userNotified = true
      deps.logError('PURCHASE-F_DECLINED: payment not successful', raced)
    }
  } catch (error) {
    // An aborted fetch (lib/api's AbortController hitting the same timeout) must
    // read as a timeout, not a generic error, so messaging is consistent no
    // matter which timer wins the race.
    if (isAbortError(error)) {
      outcome = 'timed-out'
      deps.notifyUser(TIMEOUT_MESSAGE)
      userNotified = true
      deps.logError('PURCHASE-F_TIMEOUT: payment request aborted (timeout)', error)
    } else {
      outcome = 'errored'
      deps.notifyUser(GENERIC_ERROR_MESSAGE)
      userNotified = true
      deps.logError('PURCHASE-F_ERROR: payment threw', error)
    }
  } finally {
    deps.clearCard()
    deps.setProcessing(false)
  }

  return {outcome, submissionCast, userNotified}
}
